import axios from 'axios';
import { API_BASE_URL } from '../../config';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('App Registry API Error:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

const appRegistryService = {
  getApps: () => {
    return api.get('/app-registry');
  },

  getApp: (id) => {
    return api.get(`/app-registry/${id}`);
  },

  createApp: (data) => {
    return api.post('/app-registry', data);
  },

  updateApp: (id, data) => {
    return api.put(`/app-registry/${id}`, data);
  },

  getScripts: (appId) => {
    return api.get(`/app-registry/${appId}/scripts`);
  },

  createScript: (appId, data) => {
    return api.post(`/app-registry/${appId}/scripts`, data);
  },

  getClientConfig: (appId, clientTag) => {
    return api.get(`/app-registry/${appId}/clients/${clientTag}`);
  },

  upsertClientConfig: (appId, clientTag, data) => {
    return api.put(`/app-registry/${appId}/clients/${clientTag}`, data);
  },

  // Full per-client matrix: [{ app, config|null }, ...]
  getMatrixByClientTag: (clientTag) => {
    return api.get(`/app-registry/matrix/by-client/${clientTag}`);
  },

  // App-first overview endpoints (apps-overview controller)
  getOverview: () => api.get('/apps-overview'),
  getAppClients: (configKey) => api.get(`/apps-overview/${configKey}/clients`),
  getAppScripts: (configKey) => api.get(`/apps-overview/${configKey}/scripts`),
  getAppRecentRuns: (configKey, limit = 20) =>
    api.get(`/apps-overview/${configKey}/recent-runs`, { params: { limit } }),

  // Sync job endpoints
  triggerSyncForClient: async (clientId) => {
    try {
      const res = await api.post(`/clients/${clientId}/sync-catalog`);
      return { status: res.status, data: res.data };
    } catch (err) {
      if (err.response && err.response.status === 429) {
        return { status: 429, data: err.response.data };
      }
      throw err;
    }
  },

  getSyncStatus: () => {
    return api.get('/app-catalog/sync/status');
  },

  getSyncJob: (jobId) => {
    return api.get(`/app-catalog/sync/jobs/${jobId}`);
  },

  /**
   * Trigger an on-demand full catalog refresh. Debounced globally to 15s:
   * a 429 means someone else already triggered one inside the window.
   * The underlying axios client is configured not to throw on 4xx by default
   * for this endpoint; we normalize the response so callers can branch on
   * `accepted`.
   */
  refreshNow: async () => {
    try {
      const res = await api.post('/app-catalog/refresh-now');
      return { status: res.status, data: res.data };
    } catch (err) {
      if (err.response && err.response.status === 429) {
        return { status: 429, data: err.response.data };
      }
      throw err;
    }
  },
};

export { appRegistryService };
export default appRegistryService;
